import React, { createContext, useContext, useReducer, useEffect, useCallback, useRef } from 'react';
import { UserSession, AuthState } from '../types';
import { apiClient } from '../services/api';
import toast from 'react-hot-toast';

// Try to import Auth0 hook - will be null if not available
import { useAuth0 } from '@auth0/auth0-react';


interface AuthContextType {
  state: AuthState;
  login: (token: string) => Promise<void>;
  logout: () => void;
  loginWithAuth0: () => void;
  isAuth0Available: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

type AuthAction = 
  | { type: 'LOGIN_START' }
  | { type: 'LOGIN_SUCCESS'; payload: { user: UserSession; token: string } }
  | { type: 'LOGIN_FAILURE'; payload: string }
  | { type: 'LOGOUT' }
  | { type: 'SET_LOADING'; payload: boolean };

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'LOGIN_START':
      return { ...state, loading: true, error: null };
    case 'LOGIN_SUCCESS':
      return {
        ...state,
        loading: false,
        error: null,
        user: action.payload.user,
        token: action.payload.token,
      };
    case 'LOGIN_FAILURE':
      return {
        ...state,
        loading: false,
        error: action.payload,
        user: null,
        token: null,
      };
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        token: null,
        error: null,
        loading: false,
      };
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    default:
      return state;
  }
};

const initialState: AuthState = {
  user: null,
  token: null,
  loading: true,
  error: null,
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Track if we've already attempted Auth0 validation to prevent infinite loops
  // when token validation fails (e.g., expired token)
  const hasAttemptedAuth0Validation = useRef(false);

  // Check if Auth0 is available and configured
  const isAuth0Available = !!import.meta.env.REACT_APP_AUTH0_DOMAIN &&
    !!import.meta.env.REACT_APP_AUTH0_CLIENT_ID &&
    !import.meta.env.REACT_APP_AUTH0_CLIENT_ID?.includes('your-auth0-client-id') &&
    !import.meta.env.REACT_APP_AUTH0_CLIENT_ID?.includes('placeholder');

  // Always call useAuth0 hook to follow Rules of Hooks
  // When not wrapped in Auth0Provider, this might throw an error at runtime
  // but hooks must always be called unconditionally
  const auth0 = useAuth0();

  // Only use auth0 data if we're configured and Auth0 is available
  const shouldUseAuth0 = isAuth0Available && auth0;

  const handleAuth0User = useCallback(async () => {
    if (!auth0?.isAuthenticated || !auth0.user) {
      return;
    }

    // Prevent infinite loops - only attempt validation once per session
    if (hasAttemptedAuth0Validation.current) {
      return;
    }
    hasAttemptedAuth0Validation.current = true;

    // Show loading state while fetching permissions
    dispatch({ type: 'LOGIN_START' });

    try {
      // The API accepts access tokens issued for its audience; the SDK caches them and
      // uses the refresh token when they expire
      const tokenToUse = await auth0.getAccessTokenSilently();

      // Validate token with backend and fetch real user permissions from database
      // Note: Token is passed in request body, not header, so no need to set it first
      const response = await apiClient.validateToken(tokenToUse);

      if (response.success && response.data.valid && response.data.user) {
        // Only set token in API client AFTER successful validation
        apiClient.setToken(tokenToUse);

        // Fetch the access token per request; forceRefresh skips the SDK cache after a 401
        apiClient.setTokenProvider(forceRefresh =>
          auth0.getAccessTokenSilently(forceRefresh ? { cacheMode: 'off' } : undefined)
        );

        // Use the user data from backend which has real permissions from database
        dispatch({
          type: 'LOGIN_SUCCESS',
          payload: {
            user: response.data.user,
            token: tokenToUse,
          },
        });
      } else {
        // Backend validation failed - user may not exist in database
        throw new Error('User not found or not authorized. Please contact administrator.');
      }
    } catch (err) {
      console.error('Error handling Auth0 user:', err);
      // Ensure no invalid token is stored
      apiClient.clearToken();
      const errorMessage = err instanceof Error ? err.message : 'Failed to process Auth0 authentication';
      dispatch({
        type: 'LOGIN_FAILURE',
        payload: errorMessage
      });
      // Only show toast once, not on every retry
      toast.error(errorMessage);
    }
  }, [auth0, dispatch]);

  // A request that is still unauthorized after a token refresh ends the session in place;
  // ProtectedRoute then sends the user to the login page without a full reload
  useEffect(() => {
    apiClient.setUnauthorizedHandler(() => {
      dispatch({ type: 'LOGOUT' });
      toast.error('Your session has expired. Please log in again.', { id: 'session-expired' });
    });
  }, []);

  useEffect(() => {
    // Handle Auth0 authentication if available
    if (shouldUseAuth0 && auth0 && !auth0.isLoading) {
      if (auth0.isAuthenticated && auth0.user && !state.user) {
        handleAuth0User();
        return;
      } else if (!auth0.isAuthenticated) {
        // User is not authenticated with Auth0, clear any existing state
        // Reset the validation flag so they can re-authenticate on next login
        hasAttemptedAuth0Validation.current = false;
        if (auth0.error) {
          // Auth0 rejected the login (e.g. the app isn't authorized for the API):
          // show the reason on the login page instead of silently returning to it
          console.error('Auth0 login error:', auth0.error);
          dispatch({ type: 'LOGIN_FAILURE', payload: auth0.error.message });
          return;
        }
        dispatch({ type: 'LOGOUT' });
        dispatch({ type: 'SET_LOADING', payload: false });
        return;
      } else {
        // Auth0 is loaded, set loading to false
        dispatch({ type: 'SET_LOADING', payload: false });
        return;
      }
    }
    
    // Only check sessionStorage for development mode when Auth0 is not configured
    // Security: Using sessionStorage instead of localStorage (clears on tab close)
    if (!shouldUseAuth0) {
      const storedToken = apiClient.loadDevToken();
      if (storedToken) {
        validateStoredToken(storedToken);
      } else {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    }
  }, [shouldUseAuth0, auth0?.isLoading, auth0?.isAuthenticated, auth0?.user, auth0?.error, auth0, handleAuth0User, state.user]);

  // Dev mode only: validates stored token from sessionStorage
  const validateStoredToken = async (token: string) => {
    try {
      const response = await apiClient.validateToken(token);
      if (response.success && response.data.valid) {
        // Dev mode uses sessionStorage for persistence
        apiClient.setDevToken(token);
        dispatch({
          type: 'LOGIN_SUCCESS',
          payload: {
            user: response.data.user,
            token,
          },
        });
      } else {
        // Invalid token - clear from storage
        apiClient.clearToken();
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    } catch (error) {
      console.error('Token validation failed:', error);
      apiClient.clearToken();
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  // Dev mode only: login with a development token
  // In production, use loginWithAuth0() instead
  const login = async (token: string): Promise<void> => {
    dispatch({ type: 'LOGIN_START' });

    try {
      const response = await apiClient.validateToken(token);

      if (response.success && response.data.valid) {
        // Dev mode uses sessionStorage for persistence
        apiClient.setDevToken(token);
        dispatch({
          type: 'LOGIN_SUCCESS',
          payload: {
            user: response.data.user,
            token,
          },
        });
        toast.success('Successfully logged in!');
      } else {
        throw new Error('Invalid token');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Login failed';
      dispatch({ type: 'LOGIN_FAILURE', payload: errorMessage });
      toast.error(errorMessage);
      throw error;
    }
  };

  const logout = () => {
    // Reset the validation flag so user can re-authenticate
    hasAttemptedAuth0Validation.current = false;

    // If Auth0 is available and user is authenticated, use Auth0 logout
    if (shouldUseAuth0 && auth0?.isAuthenticated) {
      auth0.logout({
        logoutParams: {
          returnTo: window.location.origin
        }
      });
    }

    apiClient.clearToken();
    dispatch({ type: 'LOGOUT' });
    toast.success('Logged out successfully');
  };

  const loginWithAuth0 = () => {
    if (shouldUseAuth0 && auth0) {
      auth0.loginWithRedirect({
        authorizationParams: {
          screen_hint: 'login',
        }
      });
    } else {
      toast.error('Auth0 is not available. Please check your configuration.');
    }
  };


  const value = {
    state,
    login,
    logout,
    loginWithAuth0,
    isAuth0Available,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};